import { useEffect, useState } from "react";
import {
  Calendar,
  MapPin,
  Clock,
  Tag,
  Search,
  Loader2,
  Info,
} from "lucide-react";
import { eventsAPI, type JBFEvent } from "../../services/api";
import eventsHeroBg from "../../assets/jbf_event1.jpg"; // Reusing an existing asset for hero background
import { formatDate } from "@/projects/Jainboarding_Admin/lib/utils";

export default function EventsPage() {
  const [events, setEvents] = useState<JBFEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");

  const categories = [
    "All",
    "Workshop",
    "Seminar",
    "Meeting",
    "Hostel",
    "Institution",
    "Boarding",
  ];

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const data = await eventsAPI.getEventList();
        setEvents(data);
      } catch (error) {
        console.error("Failed to fetch events:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const filteredEvents = events.filter(event => {
    const matchesCategory = filter === "All" || event.event_for === filter;
    const matchesSearch =
      event.event_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-[#FFF9F0]">
      {/* Hero Section */}
      <section className="relative flex min-h-[60vh] items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={eventsHeroBg}
            alt="Events hero"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#7A1E1E]/70 to-[#8B2E2E]/60" />
        </div>

        <div className="relative z-10 mx-auto max-w-[1400px] px-4 text-white sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="mb-4 text-center text-5xl font-bold lg:text-6xl">
              JBF Events
            </h1>
            <p className="text-xl leading-relaxed text-gray-100">
              Stay updated with our latest workshops, seminars, and community
              gatherings designed to foster growth, learning, and collaboration
              within the Jain hostel network.
            </p>
          </div>
        </div>
      </section>

      {/* Filter & Search Bar */}
      <section className="sticky top-20 z-20 border-b border-gray-100 bg-white/80 backdrop-blur-md">
        <div className="mx-auto max-w-[1400px] px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  className={`rounded-full px-5 py-2 text-sm font-medium transition-all ${
                    filter === cat
                      ? "bg-[#7A1E1E] text-white"
                      : "bg-[#FFF9F0] text-gray-600 hover:bg-[#7A1E1E]/10"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative w-full md:w-72">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search events..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full rounded-full border border-gray-200 bg-white px-10 py-2 text-sm focus:border-[#7A1E1E] focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Events Grid */}
      <section className="py-12 sm:py-16">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex h-64 flex-col items-center justify-center gap-4">
              <Loader2 className="h-10 w-10 animate-spin text-[#7A1E1E]" />
              <p className="font-medium text-gray-500">
                Fetching upcoming events...
              </p>
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="flex h-64 flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed border-gray-200">
              <Info className="h-12 w-12 text-gray-300" />
              <p className="text-xl font-medium text-gray-500">
                No events found matching your criteria.
              </p>
              <button
                onClick={() => {
                  setFilter("All");
                  setSearchTerm("");
                }}
                className="font-semibold text-[#7A1E1E] hover:underline"
              >
                Clear all filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {filteredEvents.map(event => (
                <div
                  key={event.id}
                  className="group flex flex-col overflow-hidden rounded-2xl bg-white shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl"
                >
                  {/* Card Header (Category Badge) */}
                  <div className="relative h-3 w-full bg-gradient-to-r from-[#7A1E1E] to-[#D4A017]" />

                  <div className="flex flex-1 flex-col p-6">
                    <div className="mb-4 flex items-center justify-between">
                      <div className="flex items-center gap-1.5 rounded-full bg-[#FFF9F0] px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[#7A1E1E]">
                        <Tag className="h-3 w-3" />
                        {event.event_for}
                      </div>
                      <div className="flex items-center gap-1 text-[12px] font-bold text-blue-600">
                        <Clock className="h-[14px] w-[14px]" />
                        {event.number_of_days} Day&apos;s
                      </div>
                    </div>

                    <h3 className="mb-3 line-clamp-2 min-h-[3.5rem] text-xl font-bold text-gray-900 transition-colors group-hover:text-[#7A1E1E]">
                      {event.event_title}
                    </h3>

                    <p className="mb-6 line-clamp-3 overflow-hidden text-sm text-gray-600">
                      {event.description}
                    </p>

                    <div className="mt-auto space-y-3 border-t border-gray-200 pt-6">
                      <div className="flex items-start gap-3">
                        <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-[#7A1E1E]" />
                        <span className="text-sm font-medium text-gray-700">
                          {formatDate(event.date)}
                        </span>
                      </div>
                      <div className="flex items-start gap-3">
                        <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#D4A017]" />
                        <span className="line-clamp-2 text-sm text-gray-600">
                          {event.address}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
